import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificacionService } from './notificacion.service';

const BASE = `${environment.apiUrl}/facturas-servicio`;

// ── Tipos ─────────────────────────────────────────────────────────

/** El paso puntual pendiente (si lo hay) vive en `pasoActual`, no en este string. */
export type EstadoFactura = 'borrador' | 'pendiente' | 'aprobada' | 'rechazada';

/** Paso de aprobación configurable por obra (Administración → tab "Pasos de aprobación") —
 *  todos los pasos son homogéneos e intercambiables, solo cambia la gente asignada. */
export interface PasoAprobacion {
  id: number;
  obraId: number;
  nombre: string;
  orden: number;
  /** Explícito, no posicional (puede haber 0, 1 o varios marcados). Aplica a cualquier motivo
   *  de rechazo, no solo respaldos incompletos. */
  recibeAlertaRechazo: boolean;
  creadoEn: string;
}

/** Etiquetas legado para historial escrito antes de la migración a pasos dinámicos
 *  (donde `historial.rol` guardaba el slug fijo en vez del nombre del paso). */
const ROL_LEGADO_LABEL: Record<string, string> = {
  admin_obra: 'Administrador de Obra',
  gerencia_construccion: 'Gerencia de Construcción',
  contabilidad: 'Contabilidad',
  finanzas: 'Finanzas',
  abastecimiento: 'Abastecimiento',
  creador: 'Creador',
};

export const ESTADO_META_FIJO: Record<'borrador' | 'aprobada' | 'rechazada', { label: string; color: string; bg: string; icon: string }> = {
  borrador:  { label: 'Borrador',  color: '#5a6678', bg: '#eef1f6', icon: 'pi-file-edit' },
  aprobada:  { label: 'Aprobada',  color: '#2e7d32', bg: '#e8f5e9', icon: 'pi-check-circle' },
  rechazada: { label: 'Rechazada', color: '#c62828', bg: '#ffebee', icon: 'pi-times-circle' },
};
const PENDIENTE_META = { color: '#bf360c', bg: '#fff3e0', icon: 'pi-clock' };

export const MODALIDADES_PAGO: { value: string; label: string }[] = [
  { value: 'transferencia',                    label: 'Transferencia' },
  { value: 'contra_factura',                   label: 'Contra Factura' },
  { value: 'documento_30',                     label: 'Documento a 30 días' },
  { value: 'documento_45',                     label: 'Documento a 45 días' },
  { value: 'documento_90',                     label: 'Documento a 90 días' },
  { value: 'anticipo_50_contra_entrega_50',    label: '50% Anticipo y 50% Contra entrega' },
  { value: 'credito_contrato_sindicado',       label: 'Crédito (contrato sindicado)' },
  { value: 'vale_vista',                       label: 'Vale vista' },
];

/** Modalidades cuyo vencimiento de pago no se puede calcular automáticamente desde
 *  fecha_factura — el formulario ofrece un campo manual opcional para estas. */
export const MODALIDADES_VENCIMIENTO_MANUAL: readonly string[] = [
  'anticipo_50_contra_entrega_50', 'credito_contrato_sindicado', 'vale_vista',
];

export const CONFIG_OBRA_DEFAULT = { diasAmarillo: 2, diasRojo: 3 };

export interface ConfigObra {
  obraId: number;
  diasAmarillo: number;
  diasRojo: number;
}

export type Semaforo = 'verde' | 'amarillo' | 'rojo';

export interface ObraFactura {
  id: number;
  nombre: string;
  ciudad?: string | null;
  estado: string;
  avance: string | number;
  empresa?: { id: number; nombre: string } | null;
  tipoObra?: { nombre: string } | null;
}

export interface HistorialFactura {
  id: number;
  rol: string;
  decision: 'enviado' | 'aprobado' | 'rechazado' | 'borrador' | 'edicion';
  comentario?: string | null;
  motivoTipo?: 'respaldos_incompletos' | 'otro' | null;
  fecha: string;
  usuario?: { id: number; nombre: string; correo?: string | null } | null;
}

export interface FacturaServicio {
  id: number;
  obraId: number;
  empresaId?: number | null;
  creadoPorId: number;
  proveedor: string;
  rutProveedor?: string | null;
  numeroFactura: string;
  numeroGuia?: string | null;
  ep?: string | number | null;
  fechaFactura: string;
  monto: string | number;
  moneda: string;
  descripcionServicio: string;
  modalidadPago: string;
  oc?: string | null;
  archivoId?: number | null;
  respaldosAdjuntos: boolean;
  estado: EstadoFactura;
  pasoActualId?: number | null;
  pasoActual?: { id: number; nombre: string; orden: number } | null;
  motivoRechazo?: string | null;
  motivoRechazoTipo?: 'respaldos_incompletos' | 'otro' | null;
  estadoPago: 'no_pagada' | 'pagada';
  fechaPago?: string | null;
  fechaVencimientoPago?: string | null;
  fechaVencimientoManual?: string | null;
  creadoEn: string;
  obra?: { id: number; nombre: string } | null;
  creadoPor?: { id: number; nombre: string; correo?: string | null } | null;
  archivo?: { id: number; nombreOriginal: string; tamanoBytes: number } | null;
  historial: HistorialFactura[];
}

/** Asignación de un aprobador a un paso dinámico de una obra. */
export interface RolFacturaObra {
  id: number;
  obraId: number;
  correo: string;
  pasoId: number;
  activo: boolean;
  creadoEn: string;
  asignadoPor?: { id: number; nombre: string } | null;
  paso?: { id: number; nombre: string; orden: number } | null;
}

export interface UsuarioBusqueda {
  id: number;
  nombre: string;
  correo: string;
}

export type OrdenCampo = 'proveedor' | 'estado' | 'fecha' | 'obra' | 'numero' | 'monto';

export interface OpcionesListado {
  obraId?: number | null;
  pagina?: number;
  porPagina?: number;
  ordenCampo?: OrdenCampo;
  ordenDir?: 'asc' | 'desc';
  agruparPorObra?: boolean;
  buscar?: string;
  /** Solo lo usa dashboardListado: filtro por paso de aprobación pendiente actual. */
  pasoActualId?: number | null;
  /** Filtro múltiple por estado (borrador/pendiente/aprobada/rechazada, según la tabla). */
  estados?: string[];
  /** Filtro múltiple por paso actual — usado en bandejaControl (Revisión → Pendientes). */
  pasoIds?: number[];
  /** Rango de fecha de creación (YYYY-MM-DD), ambos límites inclusive. */
  fechaDesde?: string;
  fechaHasta?: string;
}

export interface ListadoPaginado<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RespaldoArchivo {
  id: number;
  nombreOriginal: string;
  tamanoBytes: number;
}

export interface DashboardResumen {
  porObra: { obraId: number; obraNombre: string; total: number }[];
  aprobadas: number;
  rechazadas: number;
  enTramite: number;
  total: number;
  porPaso: { pasoId: number; pasoNombre: string; total: number }[];
}

export interface DatosFactura {
  obraId: number;
  proveedor: string;
  rutProveedor?: string;
  numeroFactura: string;
  numeroGuia?: string;
  ep?: number;
  fechaFactura: string;
  monto: number;
  moneda?: string;
  descripcionServicio: string;
  modalidadPago: string;
  oc?: string;
  fechaVencimientoManual?: string;
}

/** Campos editables desde Revisión vía editarCampos() — los de DatosFactura más los de pago,
 *  que son dos campos opcionales más de la factura (sin acción/rol de "registrar pago" aparte). */
export type CamposEditables = Partial<Omit<DatosFactura, 'obraId'>> & {
  estadoPago?: 'no_pagada' | 'pagada';
  fechaPago?: string;
};

@Injectable({ providedIn: 'root' })
export class FacturasServicioService {
  private http  = inject(HttpClient);
  private notif = inject(NotificacionService);

  /**
   * Semáforo de SLA del paso actual: días transcurridos desde la última aprobación
   * (o desde el envío, para el primer paso). Umbrales configurables por obra (Administración
   * → tab Alertas); por defecto amarillo a los 2 días, rojo a los 3. Solo aplica a facturas
   * en un paso pendiente de aprobación.
   */
  semaforo(f: FacturaServicio, config: ConfigObra = { obraId: f.obraId, ...CONFIG_OBRA_DEFAULT }): Semaforo | null {
    if (!f.historial?.length) return null;
    if (f.estado !== 'pendiente') return null;
    const ultima = [...f.historial].sort((a, b) => +new Date(b.fecha) - +new Date(a.fecha))[0];
    const dias = (Date.now() - new Date(ultima.fecha).getTime()) / (24 * 60 * 60 * 1000);
    if (dias >= config.diasRojo) return 'rojo';
    if (dias >= config.diasAmarillo) return 'amarillo';
    return 'verde';
  }

  /** Presentación del estado: para 'pendiente' arma la etiqueta a partir del paso dinámico actual. */
  estadoInfo(f: FacturaServicio): { label: string; color: string; bg: string; icon: string } {
    if (f.estado === 'pendiente') {
      return { label: `Pend. ${f.pasoActual?.nombre ?? '…'}`, ...PENDIENTE_META };
    }
    return ESTADO_META_FIJO[f.estado];
  }

  /** Traduce `historial.rol`: nombre de paso (dinámico) o slug legado (pre-migración). */
  rolLabel(rol: string): string {
    return ROL_LEGADO_LABEL[rol] ?? rol;
  }

  // ── API ─────────────────────────────────────────────────────────

  obrasDisponibles(contexto: 'mis' | 'administracion' = 'mis'): Promise<ObraFactura[]> {
    return firstValueFrom(this.http.get<ObraFactura[]>(`${BASE}/obras`, { params: { contexto } }));
  }

  private paramsObraId(obraId?: number | null): { obraId: number } | {} {
    return obraId != null ? { obraId } : {};
  }

  /** Sin obraId: agrega las facturas del usuario en todas las obras a las que tiene acceso.
   *  Paginada/ordenable/filtrable en el backend, misma barra de herramientas que Revisión. */
  misFacturas(opts: OpcionesListado = {}): Promise<ListadoPaginado<FacturaServicio>> {
    return firstValueFrom(
      this.http.get<ListadoPaginado<FacturaServicio>>(`${BASE}/mis`, { params: this.paramsListado(opts) }),
    );
  }

  private paramsListado(opts: OpcionesListado = {}): Record<string, string | number> {
    const p: Record<string, string | number> = {};
    if (opts.obraId != null) p['obraId'] = opts.obraId;
    if (opts.pagina != null) p['page'] = opts.pagina;
    if (opts.porPagina != null) p['pageSize'] = opts.porPagina;
    if (opts.ordenCampo) p['ordenCampo'] = opts.ordenCampo;
    if (opts.ordenDir) p['ordenDir'] = opts.ordenDir;
    if (opts.agruparPorObra != null) p['agrupar'] = String(opts.agruparPorObra);
    if (opts.buscar) p['q'] = opts.buscar;
    if (opts.pasoActualId != null) p['pasoId'] = opts.pasoActualId;
    if (opts.estados?.length) p['estados'] = opts.estados.join(',');
    if (opts.pasoIds?.length) p['pasoIds'] = opts.pasoIds.join(',');
    if (opts.fechaDesde) p['fechaDesde'] = opts.fechaDesde;
    if (opts.fechaHasta) p['fechaHasta'] = opts.fechaHasta;
    return p;
  }

  /** Sin obraId: agrega la bandeja de aprobación de todas las obras donde el usuario tiene rol activo. */
  bandejaControl(opts: OpcionesListado = {}): Promise<ListadoPaginado<FacturaServicio>> {
    return firstValueFrom(
      this.http.get<ListadoPaginado<FacturaServicio>>(`${BASE}/control`, { params: this.paramsListado(opts) }),
    );
  }

  /** Facturas ya decididas (aprobada/rechazada) en obras donde el usuario tiene o tuvo rol de aprobación. */
  historialRevision(opts: OpcionesListado = {}): Promise<ListadoPaginado<FacturaServicio>> {
    return firstValueFrom(
      this.http.get<ListadoPaginado<FacturaServicio>>(`${BASE}/historial-revision`, { params: this.paramsListado(opts) }),
    );
  }

  listarRespaldos(facturaId: number): Promise<RespaldoArchivo[]> {
    return firstValueFrom(
      this.http.get<RespaldoArchivo[]>(`${environment.apiUrl}/archivos`, {
        params: { modulo: 'facturas-servicio-respaldo', entidadId: facturaId },
      }),
    );
  }

  async descargarPdfHistorial(id: number, numeroFactura: string): Promise<boolean> {
    try {
      const blob = await firstValueFrom(
        this.http.get(`${BASE}/${id}/pdf-historial`, { responseType: 'blob' }),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Factura-${numeroFactura}-Historial.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    } catch {
      this.notif.mostrarToast('danger', 'Error', 'No se pudo generar el PDF del historial.');
      return false;
    }
  }

  /** Todos los pares {obraId, pasoId} activos del usuario, sin fijar una obra — para decidir visibilidad/permisos. */
  misRolesTodas(): Promise<{ obraId: number; pasoId: number; pasoNombre: string }[]> {
    return firstValueFrom(
      this.http.get<{ obraId: number; pasoId: number; pasoNombre: string }[]>(`${BASE}/mis-roles-todas`),
    );
  }

  /** Búsqueda de usuarios con permiso propio de facturas-servicio (no requiere admin del portal). */
  buscarUsuarios(q: string): Promise<UsuarioBusqueda[]> {
    if (!q.trim()) return Promise.resolve([]);
    return firstValueFrom(this.http.get<UsuarioBusqueda[]>(`${BASE}/usuarios`, { params: { q } }));
  }

  private formData(datos: Partial<DatosFactura>, archivo?: File, respaldos?: File[]): FormData {
    const fd = new FormData();
    Object.entries(datos).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') fd.append(k, String(v));
    });
    if (archivo) fd.append('archivo', archivo);
    (respaldos ?? []).forEach(r => fd.append('respaldos', r));
    return fd;
  }

  async crear(
    datos: Partial<DatosFactura>, archivo?: File, respaldos?: File[], guardarComoBorrador?: boolean,
  ): Promise<FacturaServicio | null> {
    try {
      const fd = this.formData(datos, archivo, respaldos);
      fd.append('guardarComoBorrador', String(!!guardarComoBorrador));
      return await firstValueFrom(this.http.post<FacturaServicio>(BASE, fd));
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al crear factura', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  /**
   * Edita una factura en 'rechazada' o 'borrador'. guardarComoBorrador solo tiene efecto si
   * la factura sigue en borrador: true → se guarda sin enviar; false/ausente → se envía
   * (una 'rechazada' siempre se reenvía, sin importar este flag).
   */
  async editarYReenviar(
    id: number, datos: Partial<DatosFactura>, archivo?: File, respaldos?: File[], guardarComoBorrador?: boolean,
  ): Promise<FacturaServicio | null> {
    try {
      const { obraId, ...resto } = datos;
      const fd = this.formData(resto, archivo, respaldos);
      fd.append('guardarComoBorrador', String(!!guardarComoBorrador));
      return await firstValueFrom(this.http.put<FacturaServicio>(`${BASE}/${id}`, fd));
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al guardar la factura', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  /** Solo el creador puede eliminar, y solo mientras la factura está en borrador. */
  async eliminarBorrador(id: number): Promise<boolean> {
    try {
      await firstValueFrom(this.http.delete<void>(`${BASE}/${id}`));
      return true;
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al eliminar la factura', err?.error?.message ?? 'Intenta nuevamente');
      return false;
    }
  }

  async decidir(
    id: number, decision: 'aprobado' | 'rechazado', comentario?: string, motivoTipo?: 'respaldos_incompletos' | 'otro',
  ): Promise<FacturaServicio | null> {
    try {
      return await firstValueFrom(
        this.http.post<FacturaServicio>(`${BASE}/${id}/decision`, { decision, comentario, motivoTipo }),
      );
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al registrar decisión', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  /**
   * Edición de campos desde Revisión — no reinicia el flujo ni dispara notificaciones de
   * aprobación; queda auditada en el historial. Quién puede usarlo depende del estado de la
   * factura: 'pendiente' → cualquier usuario con rol activo en la obra; 'aprobada' → solo quien
   * aprobó algún paso de esa factura puntual, o quien tiene permiso de Administración (ver
   * editarCampos() en el backend). Este es también el único mecanismo para completar
   * estadoPago/fechaPago — no existe una acción de "registrar pago" separada.
   */
  async editarCampos(id: number, cambios: CamposEditables): Promise<FacturaServicio | null> {
    try {
      return await firstValueFrom(this.http.patch<FacturaServicio>(`${BASE}/${id}/campos`, cambios));
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al guardar los cambios', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  // ── Pasos de aprobación (Administración → tab "Pasos de aprobación") ─

  listarPasos(obraId: number): Promise<PasoAprobacion[]> {
    return firstValueFrom(this.http.get<PasoAprobacion[]>(`${BASE}/pasos`, { params: { obraId } }));
  }

  async crearPaso(obraId: number, nombre: string, orden?: number): Promise<PasoAprobacion | null> {
    try {
      return await firstValueFrom(
        this.http.post<PasoAprobacion>(`${BASE}/pasos`, { obraId, nombre, ...(orden != null ? { orden } : {}) }),
      );
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al crear el paso', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  async actualizarPaso(id: number, cambios: { nombre?: string; recibeAlertaRechazo?: boolean }): Promise<PasoAprobacion | null> {
    try {
      return await firstValueFrom(this.http.patch<PasoAprobacion>(`${BASE}/pasos/${id}`, cambios));
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al actualizar el paso', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  async eliminarPaso(id: number): Promise<boolean> {
    try {
      await firstValueFrom(this.http.delete(`${BASE}/pasos/${id}`));
      return true;
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al eliminar el paso', err?.error?.message ?? 'Intenta nuevamente');
      return false;
    }
  }

  /** ids: TODOS los pasos de tipo aprobación de la obra, en el nuevo orden deseado. */
  async reordenarPasos(ids: number[]): Promise<PasoAprobacion[] | null> {
    try {
      return await firstValueFrom(this.http.patch<PasoAprobacion[]>(`${BASE}/pasos/reordenar`, { ids }));
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al reordenar los pasos', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  // ── Configuración de alertas por obra ─────────────────────────────

  configObra(obraId: number): Promise<ConfigObra> {
    return firstValueFrom(this.http.get<ConfigObra>(`${BASE}/config-obra`, { params: { obraId } }));
  }

  /** Configs de las obras en scope PARA EL CONTEXTO que las pide, para calcular el semáforo
   *  cross-obra — Dashboard y Revisión agregan cada uno un conjunto de obras distinto. */
  configsObras(contexto: 'dashboard' | 'revision'): Promise<ConfigObra[]> {
    return firstValueFrom(this.http.get<ConfigObra[]>(`${BASE}/config-obra/todas`, { params: { contexto } }));
  }

  async actualizarConfigObra(obraId: number, cambios: Partial<Omit<ConfigObra, 'obraId'>>): Promise<ConfigObra | null> {
    try {
      return await firstValueFrom(this.http.patch<ConfigObra>(`${BASE}/config-obra`, { obraId, ...cambios }));
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al guardar la configuración', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  // ── Aprobadores por paso (administración) ────────────────────────

  /** Sin obraId: agrega las asignaciones de todas las obras dentro del scope del usuario. */
  listarRoles(obraId?: number | null): Promise<RolFacturaObra[]> {
    return firstValueFrom(this.http.get<RolFacturaObra[]>(`${BASE}/roles`, { params: this.paramsObraId(obraId) }));
  }

  async crearRol(obraId: number, correo: string, pasoId: number): Promise<RolFacturaObra | null> {
    try {
      return await firstValueFrom(
        this.http.post<RolFacturaObra>(`${BASE}/roles`, { obraId, correo, pasoId }),
      );
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al asignar aprobador', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  async toggleRol(id: number, activo: boolean): Promise<RolFacturaObra | null> {
    try {
      return await firstValueFrom(
        this.http.patch<RolFacturaObra>(`${BASE}/roles/${id}`, { activo }),
      );
    } catch (err: any) {
      this.notif.mostrarToast('danger', 'Error al actualizar aprobador', err?.error?.message ?? 'Intenta nuevamente');
      return null;
    }
  }

  // ── Dashboard (solo lectura, agregado de todas las obras en scope) ─

  dashboardResumen(): Promise<DashboardResumen> {
    return firstValueFrom(this.http.get<DashboardResumen>(`${BASE}/dashboard/resumen`));
  }

  dashboardListado(opts: OpcionesListado = {}): Promise<ListadoPaginado<FacturaServicio>> {
    return firstValueFrom(
      this.http.get<ListadoPaginado<FacturaServicio>>(`${BASE}/dashboard/listado`, { params: this.paramsListado(opts) }),
    );
  }

}
